import { useState } from 'react';
import { TableBody as Body, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { CheckOutlined, CloseOutlined } from '@mui/icons-material';

import { HeaderProperties } from '../../../../shared/uiComponents/Table/tableProperties';
import { CustomAlert, Text } from '../../../../shared/uiComponents';
import { InfoRow } from '../../../../components/StyledComponents';

import {
	BillingProperties,
	MBHBillingStatus,
	ModifiedSessionProperties,
} from '../../../../redux/API/ClientAPIHelpers/billingProperties';
import { Info } from '../../../../shared/Assets/svgIcons';
import { CustomTableRow, RowTableCell } from '../../../../shared/uiComponents/Table/tableHelpers';

export const Headers: HeaderProperties[] = [
	{ id: '1', name: 'Client Name', orderBy: 'client.fullName' },
	{ id: '2', name: 'Session Date', orderBy: 'sessionDate' },
	{ id: '3', name: 'Session Type' },
	{ id: '4', name: 'Provider' },
	{ id: '5', name: 'Modified' },
	{ id: '6', name: 'Submit Date', orderBy: 'reportSubmittedAt' },
	{ id: '7', name: 'Download Date', orderBy: 'reportExportedAt' },
	{ id: '8', name: 'MBH Billing Status', orderBy: 'mbhBillingStatus' },
	{ id: '9', name: 'More Details', width: '50px' },
];

const Content = ({ info }: { info: BillingProperties; onClose: () => void }) => (
	<>
		<InfoRow>
			<Text title="Client Name: " type="psm" fontWeight="semiBold" />
			<Text title={info.client.fullName} type="psm" />
		</InfoRow>
		<InfoRow>
			<Text title="Provider Name: " type="psm" fontWeight="semiBold" />
			<Text title={info.billedBy ? info.billedBy.fullName : '-'} type="psm" />
		</InfoRow>
		<InfoRow>
			<Text title="Session Type: " type="psm" fontWeight="semiBold" />
			<Text title={info.sessionType.name} type="psm" />
		</InfoRow>
		<InfoRow>
			<Text title="Session Date: " type="psm" fontWeight="semiBold" />
			<Text title={dayjs.utc(info.sessionDate).format('MM-DD-YYYY')} type="psm" />
		</InfoRow>
		<InfoRow>
			<Text title="Submit Date: " type="psm" fontWeight="semiBold" />
			<Text title={dayjs.utc(info.reportSubmittedAt).format('MM-DD-YYYY')} type="psm" />
		</InfoRow>
		<InfoRow>
			<Text title="Download Date: " type="psm" fontWeight="semiBold" />
			<Text
				title={info.reportExportedAt ? dayjs(info.reportExportedAt).format('MM-DD-YYYY') : 'Not downloaded'}
				type="psm"
			/>
		</InfoRow>
		<InfoRow>
			<Text title="Downloaded By: " type="psm" fontWeight="semiBold" />
			<Text title={info.reportExportedBy ? info.reportExportedBy.fullName : '-'} type="psm" />
		</InfoRow>
		<InfoRow>
			<Text title="MBH Billing Status: " type="psm" fontWeight="semiBold" />
			<Text
				title={typeof info.mbhBillingStatus === 'number' ? MBHBillingStatus[info.mbhBillingStatus] : 'Not Updated'}
			/>
		</InfoRow>
		<InfoRow>
			<Text title="Location: " fontWeight="semiBold" type="psm" />
			<Text title={info.location ? info.location.name : '-'} type="psm" />
		</InfoRow>
		{info.sessionDateModifiedDetails ? (
			<>
				<Text title="Modification Details: " type="h4" font="header" fontWeight="semiBold" />
				<InfoRow>
					<Text title="Modified By: " type="psm" fontWeight="semiBold" />
					<Text title={info.sessionDateModifiedDetails.dateModifiedBy.fullName} type="psm" />
				</InfoRow>
				<InfoRow>
					<Text title="Original Date: " type="psm" fontWeight="semiBold" />
					<Text title={dayjs(info.sessionDateModifiedDetails.originalDate).format('MM-DD-YYYY')} type="psm" />
				</InfoRow>
				<InfoRow>
					<Text title="Modified Date: " type="psm" fontWeight="semiBold" />
					<Text title={dayjs(info.sessionDateModifiedDetails.dateModifiedAt).format('MM-DD-YYYY')} type="psm" />
				</InfoRow>
			</>
		) : null}
	</>
);

export const ModifiedSessionDetails = ({ item }: { item: ModifiedSessionProperties }) => {
	const [open, setOpen] = useState<boolean>(false);
	const onClose = () => setOpen(false);
	const onOpen = () => setOpen(true);

	return (
		<>
			<Tooltip title="More information">
				<CheckOutlined onClick={onOpen} />
			</Tooltip>
			<CustomAlert
				open={open}
				onClose={onClose}
				title="Change Details"
				Content={() => {
					const { originalDate, originalEndTime, originalStartTime, dateModifiedAt, dateModifiedBy } = item;

					return (
						<>
							<InfoRow>
								<Text title="Original Date" type="psm" fontWeight="semiBold" />
								<Text title={dayjs(originalDate).utc(false).format('MM/DD/YYYY')} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Original Start Time" type="psm" fontWeight="semiBold" />
								<Text title={dayjs(originalStartTime).utc(false).format('hh:mm A')} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Original End Time" type="psm" fontWeight="semiBold" />
								<Text title={dayjs(originalEndTime).utc(false).format('hh:mm A')} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Modified by" type="psm" fontWeight="semiBold" />
								<Text title={dateModifiedBy.fullName} type="psm" />
							</InfoRow>
							<InfoRow>
								<Text title="Modified Date" type="psm" fontWeight="semiBold" />
								<Text title={dayjs(dateModifiedAt).utc(false).format('MM/DD/YYYY')} type="psm" />
							</InfoRow>
						</>
					);
				}}
			/>
		</>
	);
};

const DetailedInformation = ({ info }: { info: BillingProperties }) => {
	const [infoOpen, setInfoOpen] = useState<boolean>(false);

	const closeHandler = () => setInfoOpen(false);
	const openHandler = () => setInfoOpen(true);

	return (
		<>
			<Tooltip title="More information">
				<div onClick={openHandler} style={{ width: 'max-content' }}>
					<Info />
				</div>
			</Tooltip>
			<CustomAlert
				open={infoOpen}
				onClose={closeHandler}
				title="Detailed Information"
				Content={({ onClose }) => <Content info={info} onClose={onClose} />}
			/>
		</>
	);
};

export const TableBody = ({ data }: { data: BillingProperties[] }) => (
	<Body>
		{data.map((row, index) => (
			<CustomTableRow key={index}>
				<RowTableCell>{row.client.fullName}</RowTableCell>
				<RowTableCell>{dayjs.utc(row.sessionDate).format('MM-DD-YYYY')}</RowTableCell>
				<RowTableCell>{row.sessionType.name}</RowTableCell>
				<RowTableCell>{row.billedBy ? row.billedBy.fullName : '-'}</RowTableCell>
				<RowTableCell>
					{row.sessionDateModifiedDetails ? (
						<ModifiedSessionDetails item={row.sessionDateModifiedDetails} />
					) : (
						<CloseOutlined />
					)}
				</RowTableCell>
				<RowTableCell>{dayjs(row.reportSubmittedAt).format('MM-DD-YYYY')}</RowTableCell>
				<RowTableCell>
					{row.reportExportedAt ? dayjs(row.reportExportedAt).format('MM-DD-YYYY') : 'Not downloaded'}
				</RowTableCell>
				<RowTableCell>
					{typeof row.mbhBillingStatus === 'number' ? MBHBillingStatus[row.mbhBillingStatus] : 'Not Updated'}
				</RowTableCell>
				<RowTableCell>
					<div style={{ float: 'right' }}>
						<DetailedInformation info={row} />
					</div>
				</RowTableCell>
			</CustomTableRow>
		))}
	</Body>
);
